import AppFooter from "@/components/atoms/AppFooter";
import Loader from "@/components/atoms/Loader";
import NavbarTop from "@/components/organisations/layouts/NavbarTop";
import NavbarVertical from "@/components/organisations/layouts/NavbarVertical";
import { ActionJobsProvider } from "@/contexts/ActionJobsContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { PresenceProvider } from "@/contexts/PresenceContext";
import PreventRefreshWhileOnCall from "@/components/molecules/softphone/PreventRefreshWhileOnCall";
import { SoftphoneProvider } from "@/contexts/SoftphoneContext";
import CursorsOverlay from "@/components/molecules/presence/CursorsOverlay";
import WebphoneWidget from "@/components/molecules/softphone/WebphoneWidget";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { useLazyGetMyProfileQuery } from "@/services/users";
import { FC, useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { useCookies } from "react-cookie";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";

const AuthMiddleware: FC = () => {
  const { pathname } = useLocation();
  const isChat = /^\/chat(?:\/|$)/.test(pathname);
  useEffect(() => {
    if (!isChat) return;
    const update = () =>
      document.documentElement.style.setProperty(
        "--chat-viewport-height",
        `${window.visualViewport?.height ?? window.innerHeight}px`,
      );
    document.body.classList.add("chat-route");
    update();
    window.visualViewport?.addEventListener("resize", update);
    window.addEventListener("resize", update);
    return () => {
      document.body.classList.remove("chat-route");
      document.documentElement.style.removeProperty("--chat-viewport-height");
      window.visualViewport?.removeEventListener("resize", update);
      window.removeEventListener("resize", update);
    };
  }, [isChat]);
  const user = useSelector(selectCurrentUser);
  const [cookies, _, removeCookie] = useCookies(["te_co"]);
  const [getMyProfileAsync] = useLazyGetMyProfileQuery();
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!user) {
          const token = cookies.te_co;
          if (!token) throw new Error("NO_TOKEN");
          await getMyProfileAsync(token).unwrap();
          // socket.connect();
        }
      } catch (e) {
        removeCookie("te_co", { path: "/" });
        // socket.disconnect();
        setHasError(true);
      }
    })();
  }, []);

  return user ? (
    <DndProvider backend={HTML5Backend}>
      <SoftphoneProvider key={user.id}>
        {/* key sur l'id: au changement de compte ("se connecter en tant que"),
            le provider se remonte → ferme l'ancien socket et reconnecte sous la
            nouvelle identité, en temps réel. */}
        <PresenceProvider key={user.id}>
          <ChatProvider key={user.id}>
            <ActionJobsProvider key={user.id}>
              <Container fluid className={`px-0 ${isChat ? "chat-shell" : ""}`}>
                <NavbarVertical />
                <div className="content">
                  <NavbarTop />
                  {isChat ? (
                    <div className="chat-outlet">
                      <Outlet />
                    </div>
                  ) : (
                    <Outlet />
                  )}
                  {!isChat && <AppFooter />}
                </div>
              </Container>
              <CursorsOverlay />
              <WebphoneWidget />
              <PreventRefreshWhileOnCall />
            </ActionJobsProvider>
          </ChatProvider>
        </PresenceProvider>
      </SoftphoneProvider>
    </DndProvider>
  ) : hasError ? (
    <Navigate to="/auth/login" replace />
  ) : (
    <Loader className="min-vh-100" />
  );
};

export default AuthMiddleware;
